import React, { useState, useEffect } from 'react'
import { PDFPreviewContent } from './previews/PDFPreviewContent'
import { getApiBase } from '../../utils/api'
import { getQRBranding } from '../../utils/branding'
import { Loader } from 'lucide-react'

interface PDFProductionWrapperProps {
  linkId?: string
  linkData?: any
  isPreview?: boolean
  accountSlug?: string
}

export const PDFProductionWrapper: React.FC<PDFProductionWrapperProps> = ({ 
  linkId, 
  linkData: propLinkData, 
  isPreview = false,
  accountSlug
}) => {
  const [linkData, setLinkData] = useState<any>(propLinkData || null)
  const [branding, setBranding] = useState<any>(null)
  const [loading, setLoading] = useState(!propLinkData)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!linkId || propLinkData) return

      try {
        setLoading(true)
        const API_BASE = getApiBase()
        
        // Use the appropriate endpoint based on whether we have an account slug
        const endpoint = accountSlug 
          ? `${API_BASE}/api/public/link/${accountSlug}/${linkId}`
          : `${API_BASE}/api/public/link/${linkId}`
        
        const response = await fetch(endpoint)
        
        if (!response.ok) {
          throw new Error('Link not found')
        }
        
        const data = await response.json()
        setLinkData(data)
        
        // Load branding
        const brandingConfig = await getQRBranding(data.branding_type, data.author_id)
        setBranding(brandingConfig)
      } catch (err) {
        console.error('Error loading PDF data:', err)
        setError('PDF konnte nicht geladen werden')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [linkId, propLinkData, accountSlug])

  // Load branding for preview mode
  useEffect(() => {
    const loadBranding = async () => {
      if (!propLinkData || branding) return
      
      const brandingConfig = await getQRBranding(
        propLinkData.branding_type || 'beyondpages', 
        propLinkData.author_id
      )
      setBranding(brandingConfig)
    }
    
    loadBranding()
  }, [propLinkData, branding])

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <Loader className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (error || !linkData) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <p className="text-gray-600">{error || 'Keine Daten verfügbar'}</p>
      </div>
    )
  }

  // Transform data for PDFPreviewContent
  const previewData = {
    title: linkData.title || '',
    description: linkData.metadata?.description || linkData.description || '',
    pdf_url: linkData.pdf_url || '',
    // Spread metadata if it exists (this includes showDirectly, welcomeScreen, etc.)
    ...(linkData.metadata || {}),
  }

  // Debug log to check data (only in development)
  if (process.env.NODE_ENV === 'development') {
    if (!linkData.pdf_url) {
      console.warn('PDFProductionWrapper - No PDF URL found in linkData')
    }
    if (!linkData.metadata?.showDirectly) {
      console.log('PDFProductionWrapper - showDirectly is false, download button will not be shown')
    }
  }

  // Transform branding data
  const brandingData = {
    companyName: branding?.companyName || '',
    brandingType: linkData.branding_type || 'beyondpages',
    textColor: linkData.metadata?.customColors?.textColor || branding?.textColor || '#FFFFFF',
    primaryColor: linkData.metadata?.customColors?.primaryColor || branding?.primaryColor || '#3B82F6',
    secondaryColor: linkData.metadata?.customColors?.secondaryColor || branding?.secondaryColor || '#1F2937',
  }

  // Determine color mode from metadata or use default
  const colorMode = linkData.metadata?.colorMode || 'gradient'

  return (
    <PDFPreviewContent 
      data={previewData}
      branding={brandingData}
      colorMode={colorMode}
      isPreview={isPreview}
    />
  )
}